import { useTheme } from "styled-components";
import { type Position } from "react-rnd";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import useMinMaxRef from "./useMinMaxRef";
import { type Size } from "./useResizable";
import {
  WINDOW_OFFSCREEN_BUFFER_PX,
  cascadePosition,
  centerPosition,
  isWindowOutsideBounds,
} from "../functions";
import { useWindows } from "@/contexts/windows";
import { useSession } from "@/contexts/session";
import {
  calcInitialPosition,
  getWindowViewport,
  getElementViewport,
  getElementSize,
  viewWidth,
  viewHeight,
  pxToNum,
} from "@/utils/functions";
import { useMainElements } from "@/contexts/elements";
import { useSignal } from "@/contexts/signal";
import debounce from "lodash.debounce";

type Draggable = [Position, React.Dispatch<React.SetStateAction<Position>>];

const useDraggable = (id: string, size: Size): Draggable => {
  const {
    sizes: {
      window: { cascadeOffset },
    },
  } = useTheme();
  const { windows } = useWindows();
  const { autoSizing, closing, componentWindow, initialRelativePosition } =
    windows[id] || {};
  const { events: RD_Events } = useSignal();
  const { stackOrder, windowStates: { [id]: windowState } = {} } = useSession();
  const { position: sessionPosition, size: sessionSize } = windowState || {};
  const { WindowsViewAreaSize } = useMainElements();
  const isOffscreen = useMemo(
    () =>
      isWindowOutsideBounds(windowState, {
        x: pxToNum(WindowsViewAreaSize?.width) || viewWidth(),
        y: pxToNum(WindowsViewAreaSize?.height) || viewHeight(),
      }),
    [windowState, WindowsViewAreaSize]
  );
  const [position, setPosition] = useState<Position>(
    () =>
      (!isOffscreen && sessionPosition) ||
      cascadePosition(
        id,
        windows,
        stackOrder,
        cascadeOffset,
        WindowsViewAreaSize
      ) ||
      centerPosition(size, WindowsViewAreaSize)
  );
  const blockAutoPositionRef = useMinMaxRef(id);
  const positionRef = useRef(position);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    const monitorViewportResize = debounce((newWindowsViewSize: Size): void => {
      const bounds = {
        x: pxToNum(newWindowsViewSize.width) || viewWidth(),
        y: pxToNum(newWindowsViewSize.height) || viewHeight(),
      };

      if (
        isWindowOutsideBounds(
          { position: positionRef.current, size },
          bounds,
          true
        )
      ) {
        setPosition(({ x, y }) => {
          const xOffset = bounds.x - WINDOW_OFFSCREEN_BUFFER_PX.RIGHT;
          const yOffset = bounds.y - WINDOW_OFFSCREEN_BUFFER_PX.BOTTOM;

          const newX = x > xOffset ? xOffset : x;
          const newY = y > yOffset ? yOffset : y;
          if (newX !== x || newY !== y) {
            return { x: newX, y: newY };
          }
          return { x, y };
        });
      }
    }, 20); // Debounce time in milliseconds

    const registeredEvents = [
      RD_Events.on("event:elements/resize/windowsview", monitorViewportResize),
    ];

    return () => {
      // Cleanup
      registeredEvents.forEach(RD_Events.removeListenerByID);
      monitorViewportResize.cancel(); // Cancel the debounced function
    };
  }, [size, RD_Events]);

  useLayoutEffect(() => {
    if (
      autoSizing &&
      !closing &&
      sessionSize &&
      !sessionPosition &&
      !blockAutoPositionRef.current
    ) {
      setPosition(centerPosition(sessionSize));
    }
  }, [autoSizing, blockAutoPositionRef, closing, sessionPosition, sessionSize]);

  useLayoutEffect(() => {
    if (initialRelativePosition && componentWindow && size) {
      setPosition(
        calcInitialPosition(componentWindow, initialRelativePosition, size)
      );
    }
  }, [componentWindow, initialRelativePosition, size]);

  return [position, setPosition];
};

export default useDraggable;
